import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import type { ApiErrorResponse } from '@/lib/api'

type UseAuthFormOptions<TData, TPayload> = {
  mutationFn: (payload: TPayload) => Promise<TData>
  onSuccess?: (data: TData) => void
  successMessage?: string
}

export const useAuthForm = <TData, TPayload extends Record<string, string>>({
  mutationFn,
  onSuccess,
  successMessage,
}: UseAuthFormOptions<TData, TPayload>) => {
  const mutation = useMutation<TData, AxiosError<ApiErrorResponse>, TPayload>({
    mutationFn,
    onSuccess: (data) => {
      if (successMessage) showSuccess(successMessage)
      onSuccess?.(data)
    },
  })

  const validationErrors = mutation.error?.response?.data?.validation_errors;

  const onSubmit = (event: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const payload = Object.fromEntries(
      Array.from(formData.entries()).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
    ) as TPayload
    mutation.mutate(payload)
  }

  return {
    onSubmit,
    isPending: mutation.isPending,
    validationErrors,
  }
}
